/**
 * Lifecycle of a game invite as stored in the database. Mirrors the Prisma
 * `GameInviteStatus` enum (keep them in sync). PENDING is the only live state;
 * the rest are terminal:
 * - `ACCEPTED` / `DECLINED` — the invitee responded (stamps `respondedAt`).
 * - `CANCELED` — the inviter withdrew it.
 * - `EXPIRED`  — nobody responded within the TTL. Expiry is LAZY: there is no
 *   cron; any read or mutation touching a past-`expiresAt` PENDING invite flips
 *   it to EXPIRED first.
 */
export const GAME_INVITE_STATUSES = [
  "PENDING",
  "ACCEPTED",
  "DECLINED",
  "EXPIRED",
  "CANCELED",
] as const;
export type GameInviteStatus = (typeof GAME_INVITE_STATUSES)[number];

/**
 * How long a game invite stays acceptable (`expiresAt = createdAt + TTL`).
 * Shared so the web can render an accurate countdown without hardcoding it.
 */
export const GAME_INVITE_TTL_MS = 60_000;

/**
 * The LONGEST an invite may live — the ceiling `ttlMs` is clamped to, and the window an invite
 * gets while the table has no deadline of its own to sync to.
 *
 * ⚠️ THIS IS NOT THE DEFAULT. An invite that names no `ttlMs` still gets {@link GAME_INVITE_TTL_MS}
 * (60s), because the ordinary case is a table whose fill countdown is already running and the
 * invite must die with it. The ceiling only matters for the one case that has NO deadline yet: a
 * public table waiting for its second human, where nothing is counting and an invite pinned to a
 * 60s window would expire long before anyone could act on it.
 *
 * ⚠️ NOTHING IS SUPPLIED BY A CLIENT. The API derives the real deadline from the table's own
 * published countdown on every read — `min(this ceiling, dealsAt − INVITE_DEAL_MARGIN_MS)` — so it
 * appears when a second human seats, GOES AWAY AGAIN if the countdown pauses, and comes back on a
 * resume. This value is the upper bound that stands whenever the table has no deadline of its own.
 */
export const GAME_INVITE_MAX_TTL_MS = 10 * 60_000;

/**
 * How far BEFORE its table deals an invite closes.
 *
 * A settled product rule: the invite is synced to the table's own timer and dies one second early,
 * so nobody taps «پیوستن» into a game that has just started dealing. The API applies it when it
 * derives a deadline from the table's published countdown.
 */
export const INVITE_DEAL_MARGIN_MS = 1_000;
